// FG Movement — Phase 1D-δ-2 (read-only list, mutasi barang jadi).
// Pola daftar seragam (28 Sep 2026; `docs/POLA_DAFTAR.md`): mode SERVER penuh, keadaan di alamat
// halaman, cari nomor WO di server, saringan bawaan = 30 hari terakhir + gudang sendiri (Admin Gudang).
import React, { useEffect, useState, useCallback } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { History, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { listFGMovements, apiErrorMessage } from "../../lib/inventoryApi";
import { api } from "../../lib/api";
import { formatDateTime, isoPlusDays } from "../../lib/format";
import { movementTypeClass } from "../../components/etams/StatusBadge";
import { useAuth } from "../../contexts/AuthContext";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";

const MOV_TYPES = ["PRODUCTION_IN", "TRANSFER_OUT", "TRANSFER_IN", "ADJUSTMENT", "SALE_OUT", "RETURN_IN"];
// Label untuk staf — kode mentah jenis mutasi/produk/referensi tidak tampil (K200 butir 6).
const LABEL_MOV = {
  PRODUCTION_IN: "Masuk dari produksi", TRANSFER_OUT: "Transfer keluar", TRANSFER_IN: "Transfer masuk",
  ADJUSTMENT: "Penyesuaian", SALE_OUT: "Keluar (penjualan)", RETURN_IN: "Retur masuk",
};
const LABEL_PRODUK_FG = { ATASAN: "Atasan", CELANA: "Celana" };
const labelRef = (t) => (t ? t.replace(/_/g, " ").toLowerCase() : "-");

// KONTRAS-01 Batch 9 — tiga warna keras di sini pindah ke token lewat
// `movementTypeClass()`. Tiga makna tetap tiga tone: NOL informasi hilang.
const TYPE_BADGE = (t) =>
  movementTypeClass(t?.endsWith("IN") ? "IN" : t?.endsWith("OUT") ? "OUT" : "OTHER");

//: Cermin `URUT_SAH_FG_MUTASI` di backend/services/finished_goods_service.py.
const URUT_SAH = ["created_at", "qty_change", "movement_type"];

const KOLOM_CSV = [
  { kunci: "created_at", judul: "Tanggal", ambil: (m) => formatDateTime(m.created_at) },
  { kunci: "wo", judul: "WO", ambil: (m) => m.wo?.wo_number || "" },
  { kunci: "store", judul: "Gudang", ambil: (m) => m.store?.name || "" },
  { kunci: "product_type", judul: "Produk", ambil: (m) => LABEL_PRODUK_FG[m.product_type] || m.product_type },
  { kunci: "movement_type", judul: "Tipe", ambil: (m) => LABEL_MOV[m.movement_type] || m.movement_type },
  { kunci: "qty_change", judul: "Perubahan" },
  { kunci: "qty_before", judul: "Sebelum" },
  { kunci: "qty_after", judul: "Sesudah" },
  { kunci: "reference_type", judul: "Sumber", ambil: (m) => labelRef(m.reference_type) },
];

export default function FGMovement() {
  const { roleCode, profile } = useAuth();
  const gudangSaya = (roleCode === "ADMIN_GUDANG" && profile?.store_id) || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stores").then(r => setStores(r.data || [])).catch((e) => toast.error(apiErrorMessage(e, "Data pendukung gagal dimuat.")));
  }, []);

  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      gudang: { label: "Gudang", param: "store_id", bawaan: gudangSaya,
                tampil: (v) => stores.find(s => s.id === v)?.name || "…" },
      produk: { label: "Produk", param: "product_type", tampil: (v) => LABEL_PRODUK_FG[v] || v },
      tipe: { label: "Tipe", param: "movement_type", tampil: (v) => LABEL_MOV[v] || v },
      dari: { label: "Dari", param: "date_from", bawaan: isoPlusDays(-30) },
      sampai: { label: "Sampai", param: "date_to" },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const bangunParams = useCallback((o = {}) => ({
    ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...o,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listFGMovements(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listFGMovements(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat mutasi barang jadi.")); }
    finally { setLoading(false); }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);

  const ns = daftar.nilaiSaringan;
  return (
    <div className="space-y-6" data-testid="fg-mov-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <History className="h-6 w-6" /> Mutasi Barang Jadi
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Catatan barang jadi — hanya bertambah, tidak pernah dihapus
            (produksi masuk, transfer, penyesuaian, dll).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
        </Button>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari nomor WO" testId="fg-mov-palang"
                    ekspor={{ nama: "mutasi_barang_jadi", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={ns.gudang} onChange={(e) => daftar.setSaringan("gudang", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="fg-mov-store" aria-label="Saring gudang">
          <option value="">Semua Gudang</option>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={ns.produk} onChange={(e) => daftar.setSaringan("produk", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="fg-mov-pt" aria-label="Saring produk">
          <option value="">Semua Produk</option>
          <option value="ATASAN">Atasan</option>
          <option value="CELANA">Celana</option>
        </select>
        <select value={ns.tipe} onChange={(e) => daftar.setSaringan("tipe", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="fg-mov-type" aria-label="Saring tipe">
          <option value="">Semua Tipe</option>
          {MOV_TYPES.map(t => <option key={t} value={t}>{LABEL_MOV[t]}</option>)}
        </select>
        <Input type="date" value={ns.dari} className="w-40" aria-label="Dari tanggal"
               onChange={(e) => daftar.setSaringan("dari", e.target.value)} data-testid="fg-mov-from" />
        <Input type="date" value={ns.sampai} className="w-40" aria-label="Sampai tanggal"
               onChange={(e) => daftar.setSaringan("sampai", e.target.value)} data-testid="fg-mov-to" />
      </PalangDaftar>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="created_at" className="px-4 py-3 font-medium">Tanggal</ThUrut>
                <th className="px-4 py-3 font-medium">WO</th>
                <th className="px-4 py-3 font-medium">Gudang</th>
                <th className="px-4 py-3 font-medium">Produk</th>
                <ThUrut daftar={daftar} kunci="movement_type" className="px-4 py-3 font-medium">Tipe</ThUrut>
                <ThUrut daftar={daftar} kunci="qty_change" className="px-4 py-3 font-medium" align="right">Perubahan</ThUrut>
                <th className="px-4 py-3 font-medium text-right">Sebelum</th>
                <th className="px-4 py-3 font-medium text-right">Sesudah</th>
                <th className="px-4 py-3 font-medium">Sumber</th>
              </tr>
            </thead>
            <tbody>
              {loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="9" className="p-8 text-center text-muted-foreground">Memuat...</td></tr>
              )}
              {!loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="9" className="p-12 text-center text-muted-foreground text-sm" data-testid="fg-mov-kosong">
                  {daftar.cari.trim() || daftar.saringanAktif.length
                    ? "Tidak ada mutasi yang cocok dengan pencarian atau saringan ini."
                    : "Belum ada mutasi barang jadi."}
                </td></tr>
              )}
              {daftar.tampil.map(m => (
                <tr key={m.id} className="border-b hover:bg-muted/50">
                  <td className="px-4 py-3 whitespace-nowrap text-xs">{formatDateTime(m.created_at)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{m.wo?.wo_number || "—"}</td>
                  <td className="px-4 py-3">{m.store?.name || "—"}</td>
                  <td className="px-4 py-3"><Badge variant="outline">{LABEL_PRODUK_FG[m.product_type] || m.product_type}</Badge></td>
                  <td className="px-4 py-3"><Badge className={TYPE_BADGE(m.movement_type)}>{LABEL_MOV[m.movement_type] || m.movement_type}</Badge></td>
                  <td className={`px-4 py-3 text-right font-semibold ${Number(m.qty_change) < 0 ? "text-danger-soft-foreground" : "text-success-soft-foreground"}`}>
                    {Number(m.qty_change) > 0 ? "+" : ""}{m.qty_change}
                  </td>
                  <td className="px-4 py-3 text-right">{m.qty_before}</td>
                  <td className="px-4 py-3 text-right font-semibold">{m.qty_after}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{labelRef(m.reference_type)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginasi daftar={daftar} testId="fg-mov-paginasi" />
      </Card>
    </div>
  );
}
